import { useEffect, useState } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Flag, { FlagGroup } from '@atlaskit/flag';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';

import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';
import { useFlag } from 'src/hooks/flag';
import { ComponentFlagProps } from 'src/redux/flags/types';
import { getCurrentWorkspace } from 'src/redux/profile/selectors';
import { UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY } from 'src/sections/global/components/unlinked-workspace-notifications';
import { getisUnlinkedWsFlagNotificationRelevant } from 'src/selectors/global-selectors';
import { urls } from 'src/urls';
import { captureExceptionWithTags } from 'src/utils/sentry';
import { atlassianStore as store } from 'src/utils/store';

export const UNLINKED_WORKSPACE_FLAG_ID = 'unlinked-workspace-flag' as const;

export const FLAG_RECURRENCE_TIME_MS = 1000 * 60 * 60 * 24; // 24 hours

const messages = defineMessages({
  title: {
    id: 'frontbucket.flags.unlinkedWorkspace.title',
    description: 'Text for title of flag',
    defaultMessage: 'Link this workspace to an Atlassian organization',
  },
  description: {
    id: 'frontbucket.flags.unlinkedWorkspace.description',
    description: 'Text for description of flag',
    defaultMessage:
      'Link this workspace to an Atlassian organization to take the first step and avoid delays in benefiting from upcoming features like centralized user management and unified billing across your Atlassian products.',
  },
  learnMoreText: {
    id: 'frontbucket.flags.unlinkedWorkspace.learnMoreText',
    description: 'Text for link to learn more',
    defaultMessage: 'Learn more',
  },
  linkWsText: {
    id: 'frontbucket.flags.unlinkedWorkspace.linkWsText',
    description: 'Text for link to link workspace',
    defaultMessage: 'Link workspace',
  },
});

export function useUnlinkedWorkspaceFlag() {
  const { showFlagComponent } = useFlag();

  const flagGateIsTrue = useStatsigGate(
    StatsigFeatureKeys.showUnlinkedWsFlagUi
  );

  const modalGateIsEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcUnlinkedWsModal
  );

  if (flagGateIsTrue && !modalGateIsEnabled) {
    showFlagComponent(UNLINKED_WORKSPACE_FLAG_ID);
  }
}

export const wasDismissedRecently = () => {
  const dismissedData = store.get(UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY);
  if (dismissedData && dismissedData.dismissed_at) {
    try {
      const dismissedAt: Date = new Date(dismissedData.dismissed_at);
      const now = new Date().getTime();
      const timeSinceDismissed = now - dismissedAt.getTime();

      if (timeSinceDismissed < FLAG_RECURRENCE_TIME_MS) {
        return true;
      }
    } catch (error) {
      captureExceptionWithTags(error, { dismissedData });
      return true;
    }
  }

  return false;
};

export const checkIfWorkspaceLinked = async (workspaceSlug?: string) => {
  try {
    if (!workspaceSlug) {
      throw new Error('Workspace slug is required to check link status');
    }

    const response = await fetch(
      `/!api/internal/workspaces/${workspaceSlug}/org-link-status`
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch workspace link status: ${response.statusText}`
      );
    }

    const json = await response.json();
    return json?.has_catalogue_record;
  } catch (error) {
    captureExceptionWithTags(error, { workspaceSlug: workspaceSlug || '' });
    return true; // If anything goes wrong, assume workspace is linked and don't risk showing a misleading flag
  }
};

const UnlinkedWorkspaceFlag = ({
  id,
  handleDismissed,
  ...flagProps
}: ComponentFlagProps) => {
  const intl = useIntl();
  const { dismissFlag } = useFlag();
  const currentWorkspace = useSelector(getCurrentWorkspace);
  const [okToShowFlag, setOkToShowFlag] = useState(false);
  const isFlagRelevant = useSelector(getisUnlinkedWsFlagNotificationRelevant);

  const dismiss = () => {
    const dismissedData = store.get(UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY);
    store.set(UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY, {
      ...dismissedData,
      dismissed_at: new Date(),
    });
    handleDismissed(id);
  };

  const actions = [
    {
      content: intl.formatMessage(messages.learnMoreText),
      href: urls.external.orgToWsLinkingSupportPage,
      target: '_blank',
    },
    {
      content: intl.formatMessage(messages.linkWsText),
      onClick: () => dismiss(),
      href: urls.external.orgToWsLinking,
      target: '_blank',
    },
  ];

  useEffect(() => {
    const immediatelyDismissFlag = () => {
      setOkToShowFlag(false);
      dismissFlag(UNLINKED_WORKSPACE_FLAG_ID);
    };

    if (!isFlagRelevant || wasDismissedRecently()) {
      immediatelyDismissFlag();
      return;
    }

    const checkStatusAndShowOrHideFlag = async () => {
      const isWorkspaceLinked = await checkIfWorkspaceLinked(
        currentWorkspace?.slug
      );

      if (isWorkspaceLinked) {
        immediatelyDismissFlag();
      } else {
        setOkToShowFlag(true);
      }
    };

    checkStatusAndShowOrHideFlag();
  }, [currentWorkspace?.slug, dismissFlag, isFlagRelevant]);

  if (!okToShowFlag) {
    return null;
  }

  return (
    <FlagGroup>
      <Flag
        {...flagProps}
        actions={actions}
        id={id}
        title={intl.formatMessage(messages.title)}
        description={<FormattedMessage {...messages.description} />}
        icon={<StatusWarningIcon spacing="spacious" label="Warning" />}
        onDismissed={() => dismiss()}
      />
    </FlagGroup>
  );
};

// eslint-disable-next-line import/no-default-export
export default UnlinkedWorkspaceFlag;
